import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SurveySubTab, TrashItem } from '@/types/survey.types';
import { ColumnPicker } from '../../components/ColumnPicker';
import { FundamentalsTable } from './FundamentalsTable';
import { CustomTable } from './CustomTable';
import { FUND_COLS, PERS_COLS } from './colDefs';
import { exportSurveyToExcel } from '@/utils/exportExcel';
import { surveyApi } from '@/api/client';
import { useFundamentalSurveys, useCustomSurveys, useDeleteSurvey, useDeletedSurveys, useRestoreSurvey, usePermanentDeleteSurvey, useDuplicateSurvey } from '@/hooks/useSurveys';

interface SurveysTabProps {
  onOpenBuilder: (m: { mode: 'create' } | { mode: 'edit'; surveyId: string }) => void;
}

export function SurveysTab({ onOpenBuilder }: SurveysTabProps) {
  const { t, i18n } = useTranslation();
  const [sub, setSub] = useState<SurveySubTab>('fundamentals');
  const [search, setSearch] = useState('');
  const [localDeletedIds, setLocalDeletedIds] = useState<Set<string>>(new Set());
  const [localTrashItems, setLocalTrashItems] = useState<TrashItem[]>([]);
  const [localPermanentlyDeletedIds, setLocalPermanentlyDeletedIds] = useState<Set<string>>(new Set());
  const [showTrash, setShowTrash] = useState(false);

  const { data: fundSurveys = [] } = useFundamentalSurveys();
  const { data: custSurveys = [] } = useCustomSurveys();
  const { data: deletedSurveys = [] } = useDeletedSurveys();
  const deleteSurvey = useDeleteSurvey();
  const restoreSurvey = useRestoreSurvey();
  const permanentDelete = usePermanentDeleteSurvey();
  const duplicateSurvey = useDuplicateSurvey();

  // Merge API-persisted trash with locally added items not yet reflected in server cache
  const deletedIds = useMemo(() => {
    const ids = new Set<string>(deletedSurveys.map(s => s.id));
    localDeletedIds.forEach(id => ids.add(id));
    return ids;
  }, [deletedSurveys, localDeletedIds]);

  const trash = useMemo<TrashItem[]>(() => {
    const PURGE_DAYS = 30;
    const fromApi: TrashItem[] = deletedSurveys.filter(s => !localPermanentlyDeletedIds.has(s.id)).map(s => {
      const deletedMs = s.deletedAt ? new Date(s.deletedAt).getTime() : Date.now();
      const daysLeft = Math.max(0, PURGE_DAYS - Math.floor((Date.now() - deletedMs) / 86_400_000));
      return {
        id: s.id,
        name: s.name,
        responses: s.responses,
        daysLeft,
        from: 'personalizadas' as const,
        deletedBy: s.deletedBy ?? 'Admin',
        deletedAt: s.deletedAt?.split('T')[0] ?? new Date().toISOString().split('T')[0],
      };
    });
    // Include local optimistic items not yet reflected in API cache
    const apiIds = new Set(deletedSurveys.map(s => s.id));
    const localOnly = localTrashItems.filter(x => !apiIds.has(x.id) && !localPermanentlyDeletedIds.has(x.id));
    return [...fromApi, ...localOnly];
  }, [deletedSurveys, localTrashItems, localPermanentlyDeletedIds]);

  async function handleExportExcel(id: string) {
    const all = (sub === 'fundamentals' ? fundSurveys : custSurveys) as Array<{ id: string; name: string }>;
    const survey = all.find(s => s.id === id);
    if (!survey) return;
    try {
      const lang = i18n.language?.split('-')[0] ?? 'en';
      const exportData = await surveyApi.exportSurvey(id, lang);
      exportSurveyToExcel(exportData, survey.name);
    } catch (err) {
      console.error('Export failed:', err);
    }
  }

  // Column visibility — one set per sub-tab, reset when switching
  // 'createdBy' hidden by default in Generales; 'period' hidden by default in Personalizadas
  const FUND_DEFAULT_HIDDEN = new Set(['createdBy']);
  const PERS_DEFAULT_HIDDEN = new Set(['period']);
  const [fundHidden, setFundHidden] = useState<Set<string>>(new Set(FUND_DEFAULT_HIDDEN));
  const [persHidden, setPersHidden] = useState<Set<string>>(new Set(PERS_DEFAULT_HIDDEN));
  const hidden = sub === 'fundamentals' ? fundHidden : persHidden;
  const cols   = sub === 'fundamentals' ? FUND_COLS  : PERS_COLS;
  function onToggleCol(key: string, visible: boolean) {
    const setter = sub === 'fundamentals' ? setFundHidden : setPersHidden;
    setter(prev => { const n = new Set(prev); if (visible) n.delete(key); else n.add(key); return n; });
  }
  function onResetCols() {
    sub === 'fundamentals'
      ? setFundHidden(new Set(FUND_DEFAULT_HIDDEN))
      : setPersHidden(new Set(PERS_DEFAULT_HIDDEN));
  }

  function moveToTrash(id: string, name: string, responses: number = 0) {
    const from = sub === 'fundamentals' ? 'fundamentals' : 'personalizadas';
    const today = new Date().toISOString().split('T')[0];
    setLocalDeletedIds(prev => new Set([...prev, id]));
    setLocalTrashItems(prev => [...prev, { id, name, responses, daysLeft: 30, from, deletedBy: 'Admin', deletedAt: today }]);
    deleteSurvey.mutate(id);
  }

  function restoreFromTrash(id: string) {
    setLocalDeletedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    setLocalTrashItems(prev => prev.filter(x => x.id !== id));
    restoreSurvey.mutate(id);
  }

  function handleDuplicate(id: string) {
    duplicateSurvey.mutate(id);
  }

  function deleteForever(id: string) {
    setLocalPermanentlyDeletedIds(prev => new Set([...prev, id]));
    setLocalTrashItems(prev => prev.filter(x => x.id !== id));
    permanentDelete.mutate(id);
  }

  const sharedTrashProps = {
    deletedIds,
    trash,
    onMoveToTrash: moveToTrash,
    onRestoreFromTrash: restoreFromTrash,
    onDeleteForever: deleteForever,
    showTrash,
    onCloseTrash: () => setShowTrash(false),
  };

  return (
    <div className="adm-tab-content">
      <div className="admin-header">
        <h2>{t('admin.surveysTitle')}</h2>
        <div className="admin-header-actions">
          <div className="surveys-search-wrap">
            <i className="fas fa-search surveys-search-icon" />
            <input
              type="text"
              placeholder={t('admin.searchSurvey')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="surveys-search-input"
            />
          </div>
          <button className="btn-trash" onClick={() => setShowTrash(true)}>
            <i className="fas fa-trash-restore" /> {t('admin.trash')}
            {trash.length > 0 && <span className="trash-count-badge">{trash.length}</span>}
          </button>
          <button className="btn btn-primary btn-small" onClick={() => onOpenBuilder({ mode: 'create' })}>
            <i className="fas fa-plus" /> {t('admin.newSurvey')}
          </button>
        </div>
      </div>

      {/* Tabs + Column picker in same row */}
      <div className="survey-subtabs-row">
        <div className="survey-subtabs">
          <button
            className={`survey-subtab${sub === 'fundamentals' ? ' active' : ''}`}
            onClick={() => setSub('fundamentals')}
          >
            <i className="fas fa-layer-group" /> {t('admin.fundamentalsTab')}
          </button>
          <button
            className={`survey-subtab${sub === 'personalizadas' ? ' active' : ''}`}
            onClick={() => setSub('personalizadas')}
          >
            <i className="fas fa-pen-fancy" /> {t('admin.customTab')}
          </button>
        </div>
        <ColumnPicker
          cols={cols}
          hidden={hidden}
          onToggle={onToggleCol}
          onReset={onResetCols}
        />
      </div>

      {sub === 'fundamentals' && (
        <FundamentalsTable
          search={search}
          hidden={fundHidden}
          onOpenBuilder={onOpenBuilder}
          onDuplicate={handleDuplicate}
          onExportExcel={handleExportExcel}
          mutationPending={deleteSurvey.isPending || duplicateSurvey.isPending}
          {...sharedTrashProps}
        />
      )}
      {sub === 'personalizadas' && (
        <CustomTable
          search={search}
          hidden={persHidden}
          onOpenBuilder={onOpenBuilder}
          onDuplicate={handleDuplicate}
          onExportExcel={handleExportExcel}
          mutationPending={deleteSurvey.isPending || duplicateSurvey.isPending}
          {...sharedTrashProps}
        />
      )}
    </div>
  );
}
